import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/data/constants";
import VenueCard from "@/components/VenueCard";
import VenueMap from "@/components/VenueMap";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Search as SearchIcon, MapIcon, List, X } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [areas, setAreas] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");

  useEffect(() => { api.get("/meta/areas").then(r => setAreas(r.data)); }, []);
  useEffect(() => { api.get("/meta/features").then(r => setFeatures(r.data)); }, []);

  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const area = params.get("area") || "all";
  const min_tier = params.get("min_tier") || "all";
  const selectedFeatures = useMemo(() => {
    const raw = params.get("features") || "";
    return raw ? raw.split(",").filter(Boolean) : [];
  }, [params]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };
  const toggleFeature = (f) => {
    const next = new URLSearchParams(params);
    const cur = new Set(selectedFeatures);
    if (cur.has(f)) cur.delete(f); else cur.add(f);
    const val = Array.from(cur).join(",");
    if (val) next.set("features", val); else next.delete("features");
    setParams(next, { replace: true });
  };
  const clearFeatures = () => {
    const next = new URLSearchParams(params);
    next.delete("features");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (category !== "all") qs.set("category", category);
      if (area !== "all") qs.set("area", area);
      if (min_tier !== "all") qs.set("min_tier", min_tier);
      if (selectedFeatures.length) qs.set("features", selectedFeatures.join(","));
      try {
        const { data } = await api.get(`/venues?${qs.toString()}`);
        setVenues(data);
      } finally { setLoading(false); }
    })();
  }, [q, category, area, min_tier, selectedFeatures]);

  const [inputQ, setInputQ] = useState(q);
  useEffect(() => { setInputQ(q); }, [q]);

  const submitSearch = (e) => { e.preventDefault(); update("q", inputQ); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900">Find accessible places</h1>
      <p className="mt-3 text-gray-700 max-w-2xl">Search across every inspected venue in Swindon and Wiltshire. Filter by category, area and the specific accessibility features you need.</p>

      <form onSubmit={submitSearch} className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4 relative">
          <label htmlFor="search-q" className="sr-only">Search</label>
          <SearchIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input id="search-q" value={inputQ} onChange={e => setInputQ(e.target.value)}
                 placeholder="Search venue name, area, description…"
                 className="pl-9 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="search-input"/>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="cat-sel" className="sr-only">Category</label>
          <Select value={category} onValueChange={v => update("category", v)}>
            <SelectTrigger id="cat-sel" className="h-12 border-2 border-gray-300" data-testid="filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.short}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="area-sel" className="sr-only">Area</label>
          <Select value={area} onValueChange={v => update("area", v)}>
            <SelectTrigger id="area-sel" className="h-12 border-2 border-gray-300" data-testid="filter-area"><SelectValue placeholder="All areas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="tier-sel" className="sr-only">Minimum grade</label>
          <Select value={min_tier} onValueChange={v => update("min_tier", v)}>
            <SelectTrigger id="tier-sel" className="h-12 border-2 border-gray-300" data-testid="filter-tier"><SelectValue placeholder="Any grade" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any grade</SelectItem>
              <SelectItem value="bronze">Bronze +</SelectItem>
              <SelectItem value="silver">Silver +</SelectItem>
              <SelectItem value="gold">Gold +</SelectItem>
              <SelectItem value="platinum">Platinum +</SelectItem>
              <SelectItem value="diamond">Diamond only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" className="w-full h-12 rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="search-submit">Search</Button>
        </div>
      </form>

      <fieldset className="mt-6 border border-gray-200 rounded-xl p-4" data-testid="feature-filter">
        <legend className="px-2 text-sm font-semibold text-gray-800">Filter by accessibility features</legend>
        <div className="flex flex-wrap gap-2">
          {features.map(f => {
            const active = selectedFeatures.includes(f);
            return (
              <button
                type="button"
                key={f}
                onClick={() => toggleFeature(f)}
                aria-pressed={active}
                data-testid={`feature-chip-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors duration-200 ${active
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-800 border-gray-300 hover:border-gray-900"}`}
              >
                {f}
              </button>
            );
          })}
          {selectedFeatures.length > 0 && (
            <button type="button" onClick={clearFeatures}
              className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 hover:text-gray-900"
              data-testid="feature-clear-btn">
              <X className="w-3.5 h-3.5"/> Clear features
            </button>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-500">Venues must have every selected feature to appear in results.</p>
      </fieldset>

      <div className="mt-8 flex items-center justify-between">
        <div className="text-gray-700"><span className="font-semibold text-gray-900">{venues.length}</span> {venues.length === 1 ? "result" : "results"}</div>
        <ToggleGroup type="single" value={view} onValueChange={v => v && setView(v)} className="border-2 border-gray-900 rounded-lg" data-testid="view-toggle">
          <ToggleGroupItem value="list" data-testid="view-list" className="data-[state=on]:bg-gray-900 data-[state=on]:text-white px-4"><List className="w-4 h-4 mr-2"/>List</ToggleGroupItem>
          <ToggleGroupItem value="map" data-testid="view-map" className="data-[state=on]:bg-gray-900 data-[state=on]:text-white px-4"><MapIcon className="w-4 h-4 mr-2"/>Map</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-gray-500 py-16 text-center">Loading venues…</div>
        ) : venues.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
            <p className="text-lg text-gray-700">No venues match those filters yet.</p>
            <Button asChild variant="outline" className="mt-4 rounded-full border-2 border-gray-900"><Link to="/nominate">Nominate a place</Link></Button>
          </div>
        ) : view === "list" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map(v => <VenueCard key={v.id} venue={v} />)}
          </div>
        ) : (
          <VenueMap venues={venues} />
        )}
      </div>
    </div>
  );
}
